import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppRoutes } from '@/app/routes/app_routes';
import { hasFixedBottomBar } from '@/app/routes/liveScreens';
import { denyAnalytics, grantAnalytics, isAnalyticsConfigured } from '@/lib/analytics';
import {
  getConsent,
  isConsentReviewRequested,
  requestConsentReview,
  subscribeConsent,
} from '@/lib/consent';

export const COOKIE_BANNER_HEIGHT_VAR = '--cookie-banner-h';

function bannerSnapshot(): boolean {
  return isAnalyticsConfigured() && (getConsent() === null || isConsentReviewRequested());
}

function reviewSnapshot(): boolean {
  return isConsentReviewRequested();
}

// CNIL: refusing must be as easy as accepting, so both buttons share the same look and size.
const choiceButton =
  'flex-1 sm:flex-none min-h-11 px-5 rounded-lg text-sm font-semibold bg-brand-900 text-white hover:bg-brand-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2';

// The "Manage cookies" button that reopened the banner: focus goes back to it once a choice is made,
// instead of falling to <body> when the banner unmounts.
let reviewTrigger: HTMLElement | null = null;

function choose(answer: () => void) {
  const trigger = reviewTrigger;
  reviewTrigger = null;
  answer();
  if (trigger?.isConnected) trigger.focus();
}

export function CookieBanner() {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const visible = useSyncExternalStore(subscribeConsent, bannerSnapshot, () => false);
  const reviewRequested = useSyncExternalStore(subscribeConsent, reviewSnapshot, () => false);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // The fixed banner would cover the screen's own bottom bar (live input, match review):
  // it is asked again on the next regular page.
  const shown = visible && !hasFixedBottomBar(pathname);

  // Published as --cookie-banner-h so that what sticks to the bottom of the screen (arrow details,
  // phone filter sheet) sits above the banner instead of under it.
  useEffect(() => {
    const section = sectionRef.current;
    if (!shown || !section) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(COOKIE_BANNER_HEIGHT_VAR, `${section.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    observer?.observe(section);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(COOKIE_BANNER_HEIGHT_VAR);
    };
  }, [shown]);

  // Reopened from "Manage cookies" (possibly far down a page): move focus so the banner is noticed.
  useEffect(() => {
    if (shown && reviewRequested) firstButtonRef.current?.focus();
  }, [shown, reviewRequested]);

  if (!shown) return null;

  return (
    <section
      ref={sectionRef}
      role="region"
      aria-label={t('cookies.banner.aria')}
      className="fixed inset-x-0 bottom-0 z-[60] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-xl shadow-lg p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-sm text-slate-700 leading-relaxed flex-1">
          {t('cookies.banner.text')}{' '}
          <Link
            to={AppRoutes.legal.privacy}
            // The pseudo-element widens the touch area to 44 px without breaking the sentence's line height.
            className="relative text-action underline underline-offset-2 rounded after:absolute after:inset-x-0 after:-inset-y-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action"
          >
            {t('cookies.banner.learn_more')}
          </Link>
        </p>
        <div className="flex gap-3 shrink-0">
          <button ref={firstButtonRef} type="button" className={choiceButton} onClick={() => choose(denyAnalytics)}>
            {t('cookies.banner.refuse')}
          </button>
          <button type="button" className={choiceButton} onClick={() => choose(grantAnalytics)}>
            {t('cookies.banner.accept')}
          </button>
        </div>
      </div>
    </section>
  );
}

/** Footer / settings link that reopens the banner to change the analytics choice. */
export function ManageCookiesButton({ className = '' }: { className?: string }) {
  const { t } = useTranslation('common');
  if (!isAnalyticsConfigured()) return null;
  return (
    <button
      type="button"
      className={`inline-flex items-center min-h-11 ${className} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2`}
      onClick={(event) => {
        reviewTrigger = event.currentTarget;
        requestConsentReview();
      }}
    >
      {t('cookies.manage')}
    </button>
  );
}
